import { useQuery } from '@tanstack/react-query';
import { useStampingTemplateConfigurationQuery } from '~/generated/hooks';
import type { StampingActionInfo, StampingCheckerInfo, MilestoneRewardTypeInfo } from '~/api';

// Keep FieldSchema as it's not in GraphQL types but used in components
export type FieldSchema = {
  type: 'string' | 'number' | 'select' | 'boolean';
  required?: boolean;
  default?: any;
  min?: number;
  max?: number;
  options?: string[];
  placeholder?: string;
  helperText?: string;
  displayType?: 'currency' | 'percentage' | 'text' | 'number' | null; // New field for display formatting
}

// Extend GraphQL types with proper typing for paramSchema
export interface ActionSchema extends Omit<StampingActionInfo, 'paramSchema'> {
  paramSchema: Record<string, FieldSchema>;
}

export interface CheckerSchema extends Omit<StampingCheckerInfo, 'paramSchema'> {
  paramSchema: Record<string, FieldSchema>;
}

// Use GraphQL type directly for MilestoneRewardType
export type MilestoneRewardType = MilestoneRewardTypeInfo;

interface StampingSchemasResult {
  checkers: CheckerSchema[]; // For stamping rules (conditions)
  eligibilityCheckers: CheckerSchema[]; // For customer eligibility
  actions: ActionSchema[];
  rewardTypes: MilestoneRewardType[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch stamping schemas for dynamic form generation from API
 */
export const useStampingSchemas = (): StampingSchemasResult => {
  const { data, isLoading, error, refetch } = useStampingTemplateConfigurationQuery(
    {},
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false,
    }
  );

  // Parse paramSchema from JSON string to object if needed
  const parseParamSchema = (paramSchema: any): Record<string, any> => {
    if (typeof paramSchema === 'string') {
      try {
        return JSON.parse(paramSchema);
      } catch (e) {
        // Only log in development mode
        if (import.meta.env.DEV) {
          console.warn('Failed to parse paramSchema:', e);
        }
        return {};
      }
    }
    return paramSchema || {};
  };

  // Map API response to expected format
  // Stamping rules checkers (for conditions in rules)
  const checkers: CheckerSchema[] = data?.stampingRulesCheckers?.map(checker => ({
    code: checker.code,
    name: checker.name,
    description: checker.description,
    paramSchema: parseParamSchema(checker.paramSchema)
  })) || [];

  // Eligibility checkers (for customer eligibility)
  const eligibilityCheckers: CheckerSchema[] = data?.stampingEligibilityCheckers?.map(checker => ({
    code: checker.code,
    name: checker.name,
    description: checker.description,
    paramSchema: parseParamSchema(checker.paramSchema)
  })) || [];

  const actions: ActionSchema[] = data?.stampingActions?.map(action => ({
    code: action.code,
    name: action.name,
    description: action.description,
    paramSchema: parseParamSchema(action.paramSchema)
  })) || [];

  const rewardTypes: MilestoneRewardType[] = data?.stampingMilestoneRewardTypes?.map(type => ({
    value: type.value,
    label: type.label,
    requiresValue: type.requiresValue
  })) || [];

  return {
    checkers,
    eligibilityCheckers,
    actions,
    rewardTypes,
    isLoading,
    error: error as Error | null,
    refetch: () => {
      refetch();
    }
  };
};